import { deleteTask, markTaskAsCompleted, pingTask } from "@/api/tasks";
import { checkEmployeeHierarchy } from "@/api/utils";
import { BaseExpandableRow } from "@/components/_Shared/Table/UI/ExpandableRow/ExpandableRow";
import { ExpandableRowButtonContainer } from "@/components/_Shared/Table/UI/ExpandableRow/ExpandableRowButtonContainer";
import { ExpandableRowContent } from "@/components/_Shared/Table/UI/ExpandableRow/ExpandableRowContent";
import { ExpandableRowHeader } from "@/components/_Shared/Table/UI/ExpandableRow/ExpandableRowHeader";
import { TaskProps } from "@/interfaces/tasks_types";
import { useState } from "react";
import { useAuth } from "@/App";
import { Error_Component } from "@/components/misc";
import { EditTaskButton, MarkTaskComplete, PingTaskButton, DeleteTaskButton } from "@/components/_Shared/Buttons/TaskButtons";
import { OpenFolderButton } from "@/components/_Shared/Button";

export function TaskRow({ data }: { data: TaskProps; }) {
    const [error, setError] = useState<string | null>(null)
    const { user } = useAuth()
    
    if (!user) {
        return <div>Loading...</div>
    }

    const handleDelete = async () => {
        if (confirm("Are you sure you want to delete this task?")) {
            try {
                if (!data.task_id) {
                    throw new Error("Task ID is undefined");
                }
                const response = await deleteTask(data.task_id);
                if (response === 200) {
                    alert("Task deleted successfully");
                    window.location.reload();
                } else {
                    setError("Error deleting task:" + response);
                }
            } catch (error) {
                setError("Error deleting task:" + error);
            }
        }
    };

    const handleMarkComplete = async () => {
        setError(null);
        if (data.status === "Closed") {
            setError("Task is already complete");
            return;
        }

        if (confirm("Are you sure you want to mark this task as complete?")) {
            if (!data.task_id) {
                setError("Task ID is undefined");
                return;
            }
            const response = await markTaskAsCompleted(data.task_id);
            if (response.operation === "COMPLETED") {
                window.location.reload();
            } else {
                setError("Error marking task as complete: " + response);
            }
        }
    };

    const handlePingTask = async () => {
        setError(null);
        if (!data.task_id) {
            setError("Task ID is undefined");
            return;
        } else if (data.status === "Closed") {
            setError("You cannot ping a completed task.");
            return;
        } else if (data.pinged) {
            setError("Task is already pinged. The assigned user will be reminded about this task on their dashboard.");
            return;
        }

        const response = await pingTask(data.task_id);
        if (response.operation === "COMPLETED") {
            alert("Task pinged successfully");
            window.location.reload();
        } else {
            setError("Error pinging task: " + response);
        }
    };

    const roleHierarchy = checkEmployeeHierarchy(user.role);
    const encoded_id = encodeURIComponent(data.task_id || '');

    return (
        <BaseExpandableRow>
            {error && <Error_Component errorString={error}/>}
            <ExpandableRowHeader title="Description:" />

            <ExpandableRowContent>
                <p>{data.description}</p>
            </ExpandableRowContent>

            <ExpandableRowHeader title="Category:" />

            <ExpandableRowContent>
                <p>{data.category}</p>
            </ExpandableRowContent>

            <ExpandableRowButtonContainer>
                <EditTaskButton task_id={encoded_id ?? ''} />
                <MarkTaskComplete handleComplete={handleMarkComplete} />
                <PingTaskButton handlePing={handlePingTask} />
                <OpenFolderButton folder_path={data.folder_location ?? ''} />
                {(roleHierarchy === "MANAGERS" || roleHierarchy === "ADMIN" || roleHierarchy === "IT") && <DeleteTaskButton handleDelete={handleDelete} />}
            </ExpandableRowButtonContainer>

        </BaseExpandableRow>
    );
}
